import { useMutation } from '@apollo/client';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { formatOperationError } from '@/api/formatError';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { datadogRum } from '@datadog/browser-rum';
import { Icon } from '@/components/primitives/icon/Icon';
import { Input } from '@/components/primitives/Input';
import { SubmitButton } from '@/components/primitives/SubmitButton';
import { AutoTooltip } from '@/components/primitives/AutoTooltip';
import { TimezoneDropdown } from '@/components';
import { closeDialog, setDialog } from '@/redux/reducers/dialog';
import { useAppDispatch } from '@/redux/hooks';
import clsx from 'clsx';
import { CREATE_SITE_MUTATION } from '@/components/sidebar/mutations/createSiteAndBuilding';
import { SidebarMenuSiteQuery } from './SidebarMenu';

export function CreateSiteDialog() {
  const dispatch = useAppDispatch();
  const [createSite] = useMutation(CREATE_SITE_MUTATION, {
    refetchQueries: [{ query: SidebarMenuSiteQuery }],
  });
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    formState: { isValid, isDirty, errors },
  } = useForm({
    defaultValues: {
      name: '',
      timezone: '',
    },
    mode: 'onBlur',
  });

  const watchTimezone = watch('timezone');

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={handleSubmit(async (values) => {
        setLoading(true);
        datadogRum.addAction('Create site', { source: 'sites-and-buildings' });
        try {
          await createSite({
            variables: {
              input: {
                name: values.name,
                timezone: values.timezone,
              },
            },
          });

          toast.success(`Site "${values.name}" created successfully`, { duration: 5000 });
          dispatch(closeDialog());
        } catch (error) {
          console.error('Error creating site:', error);
          toast.error(formatOperationError('create site', error), { duration: 5000 });
        } finally {
          setLoading(false);
        }
      })}
    >
      <h1 className="text-base text-content-primary">Add A New Site</h1>
      <div className="w-full py-2">
        <span className="text-content-primary text-sm ml-0 mb-1 block">Name:</span>
        <Input
          {...register('name', { required: "Site name can't be empty." })}
          placeholder="Please enter a site name"
          label=""
          wrapperClassName="w-full"
          errorMessage={errors.name && errors.name.message}
        />
      </div>
      <div className="mb-4">
        <span className="text-content-primary text-sm ml-0 mb-2">Timezone:</span>
        <TimezoneDropdown
          {...register('timezone', { required: 'Timezone is required, please select one.' })}
          onSelect={(name) => {
            setValue('timezone', name, { shouldDirty: true, shouldValidate: true });
            clearErrors('timezone');
          }}
          value={watchTimezone}
          errors={!!errors.timezone}
        />
        {!!errors.timezone && <div className="text-red-500 text-xs">{errors.timezone.message}</div>}
      </div>

      <div className="flex justify-end gap-4">
        <button
          type="button"
          className="rounded-md px-4 py-1 text-sm text-content-primary hover:bg-surface-hover border border-default"
          onClick={() => dispatch(closeDialog())}
        >
          Cancel
        </button>
        <SubmitButton
          color="red"
          variant="solid"
          loading={loading}
          loadingContent="Creating"
          disabled={!isValid || !isDirty}
        >
          Create Site
        </SubmitButton>
      </div>
    </form>
  );
}

export function CreateSiteButton({ variant = 'default' }: { variant?: 'default' | 'compact' }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const onClick = async () =>
    dispatch(
      setDialog({
        content: <CreateSiteDialog />,
        options: {
          className: 'w-96 rounded-[15px]',
        },
      }),
    );
  const isCompact = variant === 'compact';
  return (
    <AutoTooltip label="Create Site">
      <button
        onClick={onClick}
        className={clsx(
          'whitespace-nowrap rounded flex items-center text-sm justify-center text-content-primary hover:bg-surface-hover border border-default',
          variant === 'default' ? 'py-2 px-4' : '',
          isCompact ? 'py-2 px-2 mb-4' : '',
        )}
      >
        <Icon name="plus" className={clsx('w-4', { 'mr-2': !isCompact })} color="black"></Icon>
        {!isCompact && t('create')}
      </button>
    </AutoTooltip>
  );
}
